import { useLatest, usePrevious, useUpdateEffect } from 'ahooks';
import React from 'react';
import { Box, Spinner } from '@apia/theme';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { informationActions } from '../../../../store/informationSlice';
import debugDispatcher from '../../../../utils/debugDispatcher';
import { enforceFocus } from '../../../../utils/dom';
import { addBoundary } from '../../../../utils/number';
import shortcutController from '../../../../classes/shortcutController';
import EventEmitter from '../../../../utils/util/eventEmitter';
import Overlay from './Overlay';
import LinearLoader from '../layout/LinearLoader';
import parameters from '../../../../config/parameters';

export const lockController =
  new (class LockControllerClass extends EventEmitter<{
    forceLock: null;
    forceUnlock: null;
    forceWhiteScreen: null;
    lock: null;
    unlock: null;
    lockLinear: null;
  }> {})();

window.lockController = lockController;

/**
 * There it exists a hook which handles much better the lock process than this
 * method, which is only intended to use outside React components. Please, if
 * you will call the lock function within a component:
 *
 * @see useScreenLock
 *
 * The lockScreen method will add 1 to a counter, which tells the locking screen
 * how much components expect the screen to be locked. The only way the screen
 * is unlocked, is that this counter is exactly 0 and that there is no component
 * forcing the lock.
 *
 * @see forceScreenLock
 *
 * @returns A method which can be used to be sure that the unlocking process will be only made one time, which ensures that any component will interfere with the locking fired by other components.
 *
 * @example
 *
 * const unlock = lockScreen();
 *
 * // Make some heavy stuff
 *
 * // The unlock method is being called 3 times, but the unlock process is executed once.
 * unlock();
 * unlock();
 * unlock();
 */
export function lockScreen(linear?: boolean) {
  lockController.emit(linear ? 'lockLinear' : 'lock', null);

  let hasUnlocked = false;

  return () => {
    if (hasUnlocked) return;
    hasUnlocked = true;
    lockController.emit('unlock', null);
  };
}

/**
 * This method is the counterpart of lockScreen and must (preferably) not be
 * used, use the useScreenLock hook or the method returned from lockScreen in
 * order to avoid interfering with other components locking process.
 *
 * @see lockScreen
 * @see useScreenLock
 */
export function unlockScreen() {
  lockController.emit('unlock', null);
}

/**
 * This method is very determinant: the screen will be locked no matter what is
 * happening with other components. To do that, a flag is raised telling the
 * screen lock component: lock the screen.
 *
 * This flag can only be lowered by calling forceUnlockScreen
 *
 * @see releaseScreenLock
 */
export function forceScreenLock() {
  lockController.emit('forceLock', null);
}

/**
 * This method is very determinant: the screen will be locked no matter what is
 * happening with other components. To do that, a flag is raised telling the
 * screen lock component: lock the screen.
 *
 * This flag can only be lowered by calling forceUnlockScreen
 *
 * @see releaseScreenLock
 */
export function forceWhiteScreen() {
  lockController.emit('forceWhiteScreen', null);
}

/**
 * This method is the counterpart of forceLockScreen. If the screen is being
 * forced to be locked, this method will release it to allow the screen locking
 * process to behave normally.
 *
 * @see forceScreenLock
 */
export function releaseScreenLock() {
  lockController.emit('forceUnlock', null);
}

/**
 * This hook is the ideal way to control the screen locking process, it
 * guarantees that the screen will only be unlocked by the component which
 * is using it if it has locked it previously. This way the behavior will be
 * persistent with what the components expect to happen when they call lock or
 * unlock screen.
 *
 * @returns an object containing { lockScreen, unlockScreen } methods that are
 * explained by their names.
 */
export function useScreenLock() {
  const [isLocked, setIsLocked] = React.useState(false);
  const wasLocked = usePrevious(isLocked);

  const unlock = React.useCallback(() => {
    setIsLocked(false);
  }, [setIsLocked]);

  const lock = React.useCallback(() => {
    setIsLocked(true);
  }, [setIsLocked]);

  useUpdateEffect(() => {
    if (isLocked && !wasLocked) {
      lockScreen();
    } else if (!isLocked && wasLocked) {
      unlockScreen();
    }
  }, [isLocked]);

  return { lockScreen: lock, unlockScreen: unlock };
}

debugDispatcher.on(
  'lockScreen',
  ([linear]) => {
    lockScreen(linear as boolean);
  },
  '',
);
debugDispatcher.on(
  'unlockScreen',
  () => {
    unlockScreen();
    releaseScreenLock();
  },
  '',
);
shortcutController.on(
  'lock'.split(''),
  () => {
    releaseScreenLock();
    unlockScreen();
  },
  'dev',
);

/**
 * **DO NOT USE THIS COMPONENT**
 *
 * This component is not intended to be used by any component more than the
 * BaseApp. If you are looking for a suspense screen to use with a lazy
 * component, please refer to ScreenLockSuspense.
 *
 * **DO NOT USE THIS COMPONENT**
 *
 * @see ScreenLockSuspense
 *
 * **DO NOT USE THIS COMPONENT**
 */
const ScreenLock = () => {
  const [locks, setLocks] = React.useState(0);
  const ref = React.useRef<SVGSVGElement>(null);
  const [opacity, setOpacity] = React.useState(1);
  const [isForced, setIsForced] = React.useState(true);
  const [hasReleasedFirstTime, setHasReleasedFirstTime] = React.useState(false);
  const formSubmitAction = useAppSelector(
    (state) => state.fieldEventsSlice.formSubmitAction,
  );
  const [isLinearLocked, setIsLinearLocked] = React.useState(false);

  React.useLayoutEffect(() => {
    if (hasReleasedFirstTime) {
      if (parameters.performance.measureTime) {
        console.timeEnd('Templater pre screen unlock');
        console.timeEnd('pageLoad');
      }
      firstTimeCbs.forEach((el) => el());
    }
  }, [hasReleasedFirstTime]);

  const latestLocks = useLatest(locks);
  const previousLocks = usePrevious(locks);
  const latestPreviousLocks = useLatest(previousLocks);
  const latestForced = useLatest(isForced);

  const dispatch = useAppDispatch();
  useUpdateEffect(
    function informToRedux() {
      dispatch(
        informationActions.update({
          id: 'ScreenLock',
          locks,
          isForced,
          isLocked: isForced || locks > 0,
        }),
      );
    },
    [locks, isForced],
  );

  const actionState = useAppSelector(
    (global) => global.appStateSlice.actionState,
  );
  const previousActionState = usePrevious(actionState);

  useUpdateEffect(
    function lockOnNavigationInProcess() {
      if (actionState && !previousActionState) {
        setLocks((current) => current + 1);
      } else if (!actionState) {
        setLocks((current) => addBoundary(current - 1, 0));
      }
    },
    [actionState],
  );

  useUpdateEffect(
    function handleAppFocus() {
      if (
        !latestForced.current &&
        latestLocks.current === 0 &&
        (latestPreviousLocks.current ?? 0) > 0
      ) {
        setOpacity(0);
      } else {
        if (latestLocks.current > 0) setOpacity(1);
        if (
          ref.current &&
          latestLocks.current > 0 &&
          latestPreviousLocks.current === 0
        ) {
          void enforceFocus(ref.current as unknown as HTMLElement);
        }
      }
    },
    [locks],
  );

  useUpdateEffect(
    function lockScreenOnJavaSubmitEvent() {
      if (formSubmitAction) setIsForced(true);
    },
    [formSubmitAction],
  );

  React.useEffect(function handleLockEvents() {
    function handleLock() {
      setLocks((current) => current + 1);
    }
    function handleLockLinear() {
      setIsLinearLocked(true);
    }
    function handleUnlock() {
      setLocks((current) => addBoundary(current - 1, 0));
      setIsLinearLocked(false);
    }
    function handleForce() {
      setIsForced(true);
    }
    function handleForceWhite() {
      setHasReleasedFirstTime(false);
      setIsForced(true);
    }
    function handleRelease() {
      setIsForced(false);
      setHasReleasedFirstTime(true);
      hasReleased = true;
    }
    isScreenLockReady = true;
    readyCbs.forEach((cb) => cb({ releaseScreenLock: handleRelease }));
    const suscriptions = [
      lockController.on('lock', handleLock),
      lockController.on('lockLinear', handleLockLinear),
      lockController.on('unlock', handleUnlock),
      lockController.on('forceLock', handleForce),
      lockController.on('forceUnlock', handleRelease),
      lockController.on('forceWhiteScreen', handleForceWhite),
    ];

    return () => suscriptions.forEach((unsuscribe) => unsuscribe());
  }, []);

  const overlaySx = React.useMemo(
    () => ({
      backgroundColor: 'transparent',
      '&:not(body.editionForm *)': {
        backgroundColor: isLinearLocked
          ? 'transparent'
          : hasReleasedFirstTime
          ? 'palette.background.overlay'
          : 'palette.background.default',
      },
    }),
    [hasReleasedFirstTime, isLinearLocked],
  );

  if (locks === 0 && !isForced && !isLinearLocked) return null;

  return (
    <Box
      sx={{
        opacity,
      }}
      data-testid="Overlay"
    >
      <Overlay
        isOpen
        onExited={() => {}}
        onOverlayClicked={() => {}}
        sx={overlaySx}
        className={`${hasReleasedFirstTime ? '' : 'not-released'} screenLock`}
        transitionDuration={hasReleasedFirstTime ? undefined : 0}
      >
        {isLinearLocked ? (
          <LinearLoader />
        ) : (
          <Spinner ref={ref} sx={{ '*': { color: 'primary' } }} />
        )}
        <Box tabIndex={0} title={window.WAIT_A_SECOND} />
      </Overlay>
    </Box>
  );
};

let isScreenLockReady = false;
interface IReadyTools {
  /**
   * Con este método se libera el bloqueo de la pantalla,
   * y es pasado como parámetro de la función onScreenLockReady
   * solamente cuando la pantalla está lista para ser
   * desbloqueada.
   */
  releaseScreenLock: () => void;
}
type TReadyCb = (tools: IReadyTools) => void;
const readyCbs: TReadyCb[] = [];
/**
 * Permite agendar un callback para que sea ejecutado cuando
 * la pantalla esté lista para ser desbloqueada. Este callback
 * será llamado con un método como parámetro que ejecuta el
 * desbloqueo.
 *
 * Si la pantalla ya está lista para ser desbloqueada cuando se
 * registre el callback, éste sera llamado inmediatamente.
 */
export function onScreenLockReady(cb: TReadyCb) {
  if (!isScreenLockReady) readyCbs.push(cb);
  else cb({ releaseScreenLock });
}
let hasReleased = false;
const firstTimeCbs: (() => void)[] = [];
/**
 * Permite agendar un callback para que sea ejecutado una vez
 * que la pantalla haya sido desbloqueada por primera vez.
 *
 * Si al momento de registrar el callback la pantalla ya fue
 * desbloqueada, el callback se ejecuta inmediatamente.
 */
export function onScreenLockReleasedFirstTime(cb: () => void) {
  firstTimeCbs.push(cb);
  if (hasReleased) cb();
}
window.onScreenLockReleasedFirstTime = onScreenLockReleasedFirstTime;

export function hasReleasedScreenLock() {
  return hasReleased;
}

export default ScreenLock;
